import { useId } from "react";

import { cn } from "@/lib/utils";

export interface SparklineProps {
  /** Datapoint da plottare (minimo 2). Se vuoto: render placeholder vuoto. */
  values: readonly number[];
  /** Label accessibile per screen reader (es. "Trend ultimi 7 giorni"). */
  ariaLabel: string;
  /** Tinta della linea (default `var(--primary)`). */
  stroke?: string;
  className?: string;
}

/**
 * Sparkline SVG inline — niente chart-lib (no recharts/d3, ~zero KB).
 * Renderizza una polyline normalizzata a width=100/height=20 con stretch
 * via `preserveAspectRatio="none"` (responsive senza JS resize observer).
 *
 * Quando i `values` collassano (max === min), disegna una linea piatta a
 * 50% per evitare division-by-zero + flicker.
 */
export function Sparkline({
  values,
  ariaLabel,
  stroke,
  className,
}: SparklineProps): JSX.Element {
  const labelId = useId();

  if (values.length < 2) {
    return (
      <svg
        viewBox="0 0 100 20"
        preserveAspectRatio="none"
        className={cn("h-4 w-full text-muted-foreground/40", className)}
        aria-labelledby={labelId}
        role="img"
      >
        <title id={labelId}>{ariaLabel} (insufficient data)</title>
        <line x1="0" y1="10" x2="100" y2="10" stroke="currentColor" strokeDasharray="2 2" strokeWidth="1" />
      </svg>
    );
  }

  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;
  const step = 100 / (values.length - 1);
  const points = values
    .map((v, i) => {
      const x = i * step;
      // SVG y cresce verso il basso: invertiamo per "verso l'alto = piu' alto".
      const y = 20 - ((v - min) / range) * 18 - 1;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(" ");

  return (
    <svg
      viewBox="0 0 100 20"
      preserveAspectRatio="none"
      className={cn("h-4 w-full text-primary", className)}
      aria-labelledby={labelId}
      role="img"
    >
      <title id={labelId}>{ariaLabel}</title>
      <polyline
        points={points}
        fill="none"
        stroke={stroke ?? "currentColor"}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}
